import { zodResolver } from '@hookform/resolvers/zod';
import moment from 'moment';
import React, { useCallback, useEffect, Fragment } from 'react';
import { useForm } from 'react-hook-form';

import {
  Box,
  Button,
  Form,
  FormField,
  FormRow,
  FormSection,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  Select,
  Text,
} from '../../ui';
import { stringToNumber } from '../../../lib/zod-util';
import type { ClientId } from '../ClientId';
import type { VoyageSuggestionParams } from './VoyageSuggestionParams';
import {
  Objective,
  voyageSuggestionParamsSchema,
  getVoyageSuggestionParamDefaults,
} from './VoyageSuggestionParams';
import type { Vessel } from '../Vessel';
import { useLocalSetting } from '../../hooks/useLocalConfig';
import type { ModalWithResultProps } from '../../hooks/useModalWithResult';
import { useIsMobileView } from '../../hooks/useMediaQuery';

export type SuggestVoyageModalParams = {
  fleetVessels: Vessel[];
  shownVessels: Vessel[];
  /** Set as default on the modal, but not enforced */
  defaultValues?: Partial<VoyageSuggestionParams>;
} & ( // Specific suggestions on a single vessel, optionally adding to a shipment
  | {
      vessel: Vessel;
      firstAllowedTime: Date;
      addToVoyage:
        | {
            shipmentId: ClientId;
            startLocationName: string;
            endLocationName: string;
          }
        | undefined;
    }
  // Suggestions for all or shown vessels
  // Suggestions for a set of selected orders
  | {
      vessel?: undefined;
      addToVoyage?: undefined;
      firstAllowedTime?: undefined;
    }
);

export type SuggestVoyageModalResult = VoyageSuggestionParams & {
  vessels: Vessel[];
};

type SelectOption<T extends string> = {
  value: T;
  label: string;
  disabled?: boolean;
};
const ORDER_SELECTION_OPTIONS: SelectOption<
  SuggestVoyageModalResult['orderSelection']
>[] = [
  {
    value: 'all',
    label: 'All orders (ignore selection and filter)',
  },
  {
    value: 'only_visible',
    label: 'Currently visible orders',
  },
  {
    value: 'only_selected',
    label: 'Current selection only',
  },
  {
    value: 'exclude_selected',
    label: 'Current selection excluded',
  },
];
const ORDER_TYPES_OPTIONS: SelectOption<
  keyof SuggestVoyageModalResult['orderTypes']
>[] = [
  {
    value: 'inquiries',
    label: 'Inquiries',
  },
  {
    value: 'coaPreliminaries',
    label: 'COA preliminaries',
  },
  {
    value: 'fixedCoas',
    label: 'COA confirmed',
  },
  {
    value: 'fixedSpots',
    label: 'Spot fixed',
  },
];
const VOYAGE_TYPE_OPTIONS: SelectOption<
  SuggestVoyageModalResult['voyageType']
>[] = [
  {
    value: 'new_voyage',
    label: 'New voyages',
  },
  {
    value: 'existing_voyage',
    label: 'Additional parcels to voyages',
  },
  {
    value: 'new_or_existing_voyage',
    label: 'Both: New voyages and additional parcels',
  },
];

export type SuggestVoyageModalProps = ModalWithResultProps<
  SuggestVoyageModalResult,
  SuggestVoyageModalParams
>;

export function SuggestVoyageModal(props: SuggestVoyageModalProps) {
  const [savedDefaultValues, setDefaultValues] = useLocalSetting(
    'suggestVoyageModalConfig',
    getVoyageSuggestionParamDefaults(),
  );

  const {
    addToVoyage,
    vessel,
    onCancel,
    firstAllowedTime = new Date(),
  } = props;

  const defaultValues: VoyageSuggestionParams = {
    ...savedDefaultValues,
    voyageType: addToVoyage
      ? 'new_or_existing_voyage'
      : savedDefaultValues.voyageType,
    firstAllowedTime: firstAllowedTime.valueOf(),
    ...props.defaultValues,
  };

  const uf = useForm<SuggestVoyageModalResult>({
    defaultValues,
    resolver: zodResolver(voyageSuggestionParamsSchema),
    mode: 'onChange',
  });

  const {
    register,
    watch,
    clearErrors,
    setValue,
    formState: { errors },
  } = uf;

  useEffect(() => {
    const subscription = uf.watch((newValues) => {
      const parseResult = voyageSuggestionParamsSchema.safeParse(newValues);

      if (parseResult.success) {
        setDefaultValues(parseResult.data);
      } else {
        console.error(
          `Failed to parse form data on SuggestVoyageModal: ${parseResult.error}`,
        );
      }

      if (
        (newValues.numOrders ?? 1) > 1 &&
        newValues.objective !== Objective.MaximizeVCP
      ) {
        // Currently we only support VCP for multi parcel voyages
        setValue('objective', Objective.MaximizeVCP);
      } else {
        clearErrors('objective');
      }
    });

    return () => subscription.unsubscribe();
  });

  const orderSelection = watch('orderSelection');
  const voyageType = watch('voyageType');
  const vesselType = watch('vesselType');
  const numOrders = watch('numOrders');

  const commodityExclusionOptions: SelectOption<
    VoyageSuggestionParams['cargoExclusions']
  >[] = [
    {
      value: 'exclude',
      label: 'Exclude: no suggestions with violations',
    },
    {
      value: 'warn',
      label: 'Warn: allowed, warning shown',
    },
  ];

  const suggestionRankingOptions: SelectOption<Objective>[] = [
    {
      value: Objective.MaximizeVCP,
      label: 'Voyage cargo performance VCP',
    },
    {
      value: Objective.MaximizeTCE,
      label: `Voyage TCE`,
      disabled: numOrders > 1,
    },
    {
      value: Objective.ShortestBallast,
      label: `Shortest ballast`,
      disabled: numOrders > 1,
    },
  ];

  const VESSEL_TYPE_OPTIONS: SelectOption<'shown' | 'all' | 'selected'>[] = [
    {
      value: 'shown',
      label: 'Shown vessels',
    },
    {
      value: 'all',
      label: 'All vessels in fleet',
    },
    {
      value: 'selected',
      label: 'Selected vessel',
      disabled: !vessel,
    },
  ];

  const onConfirm = useCallback(() => {
    const values = uf.getValues();
    const { vesselType } = values;
    const { vessel, onComplete, fleetVessels, shownVessels, firstAllowedTime } =
      props;
    const selectedVessel = (vesselType === 'selected' && vessel) || null;
    onComplete({
      ...values,
      vessels:
        vesselType === 'all'
          ? fleetVessels
          : vesselType === 'shown'
            ? shownVessels
            : selectedVessel
              ? [selectedVessel]
              : [],
      firstAllowedTime:
        (selectedVessel && firstAllowedTime)?.valueOf() ?? Date.now(),
    });
  }, [props, uf]);

  const selectedVessel = (vesselType === 'selected' && vessel) || null;

  const vesselName = selectedVessel
    ? (selectedVessel.name ?? 'Vessel name missing')
    : vesselType === 'shown'
      ? 'shown vessels'
      : 'all vessels';

  const canAddToVoyage = selectedVessel && addToVoyage !== undefined;

  const newVoyage = voyageType === 'new_voyage';

  const addToVoyageName =
    canAddToVoyage && !newVoyage
      ? `(${addToVoyage.startLocationName}-${addToVoyage.endLocationName})`
      : '';

  const isMobileView = useIsMobileView();

  return (
    <Modal
      isOpen={true}
      onClose={onCancel}
      width={isMobileView ? '100%' : '600px'}
      height={isMobileView ? '100%' : 'auto'}
      maxHeight="90vh"
    >
      <ModalBody overflowY="auto">
        <Text fontWeight="bold" mb={4}>
          {addToVoyageName ? 'Add cargo' : 'Suggest voyage'}
          {' on ' + vesselName}
          {addToVoyageName}
        </Text>

        <Form>
          <FormSection>
            <Box display="flex" flexDirection={isMobileView ? 'column' : 'row'}>
              <Box width="100%">
                <FormRow>
                  <FormField label="Order selection" width={320}>
                    {ORDER_SELECTION_OPTIONS.map(({ value, label }) => (
                      <Fragment key={value}>
                        <Box display="flex" marginBottom={2}>
                          <Input
                            id={`orderSelection-${value}`}
                            type="radio"
                            size={16}
                            {...register('orderSelection')}
                            value={value}
                            data-testid={`orderSelection.${value}`}
                          />
                          <label
                            htmlFor={`orderSelection-${value}`}
                            style={{
                              marginLeft: '0.5em',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {label}
                          </label>
                        </Box>
                      </Fragment>
                    ))}
                  </FormField>
                </FormRow>
                <FormRow>
                  <FormField label="Order types" width={320}>
                    <Box
                      display="flex"
                      flexDirection="row"
                      flexWrap={isMobileView ? 'wrap' : 'nowrap'}
                    >
                      {ORDER_TYPES_OPTIONS.map(({ value: id, label }) => (
                        <Fragment key={id}>
                          <Box display="flex">
                            <Input
                              id={`orderTypes.${id}`}
                              type="checkbox"
                              {...register(`orderTypes.${id}`)}
                              data-testid={`orderTypes.${id}`}
                              disabled={orderSelection === 'only_selected'}
                              style={{
                                position: 'relative',
                                bottom: '-2px',
                                height: '16px',
                              }}
                            />
                            <label
                              htmlFor={`orderTypes.${id}`}
                              key={id}
                              style={{
                                marginLeft: '0.5em',
                                marginRight: '1em',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {label}
                            </label>
                          </Box>
                        </Fragment>
                      ))}
                    </Box>
                  </FormField>
                </FormRow>
                <FormRow>
                  <FormField
                    label="Max port pairs"
                    title="We try this many random orders after one another to find good combinations. But we also complement the result with several orders which can be added without any new legs to the voyage, so the result will typically include many more orders than the selection here indicates."
                    width={320}
                    hasError={!!errors.numOrders}
                  >
                    <Input
                      type="number"
                      min={1}
                      max={50}
                      {...register('numOrders', {
                        setValueAs: stringToNumber,
                      })}
                      data-testid={`numOrders`}
                    />
                  </FormField>
                </FormRow>
              </Box>
              <FormRow>
                <FormField
                  label="Commodity exclusions"
                  width={320}
                  hasError={!!errors.cargoExclusions}
                >
                  <Select
                    {...register('cargoExclusions')}
                    options={commodityExclusionOptions}
                    required
                  />
                </FormField>
              </FormRow>
            </Box>
          </FormSection>
          <FormSection>
            <Box display="flex" flexDirection="column">
              <Box
                display="flex"
                flexDirection={isMobileView ? 'column' : 'row'}
                width="100%"
              >
                <FormRow>
                  <FormField label="Vessel scope">
                    {VESSEL_TYPE_OPTIONS.map(({ value, label, disabled }) => (
                      <Fragment key={value}>
                        <Box display="flex" marginBottom={2}>
                          <Input
                            id={`vesselType-${value}`}
                            type="radio"
                            size={16}
                            {...register('vesselType')}
                            value={value}
                            data-testid={`vesselType.${value}`}
                            disabled={disabled}
                          />
                          <label
                            htmlFor={`vesselType-${value}`}
                            style={{
                              marginLeft: '0.5em',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {label}
                          </label>
                        </Box>
                      </Fragment>
                    ))}
                  </FormField>
                </FormRow>
                <Box width="100%">
                  <FormRow>
                    <FormField label="Suggestion type">
                      {VOYAGE_TYPE_OPTIONS.map(({ value, label }) => (
                        <Fragment key={value}>
                          <Box display="flex" marginBottom={2}>
                            <Input
                              id={`voyageType-${value}`}
                              type="radio"
                              size={16}
                              {...register('voyageType')}
                              value={value}
                              data-testid={label}
                            />
                            <label
                              htmlFor={`voyageType-${value}`}
                              style={{
                                marginLeft: '0.5em',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {label}
                            </label>
                          </Box>
                        </Fragment>
                      ))}
                    </FormField>
                  </FormRow>

                  <FormRow
                    marginLeft={2}
                    marginTop={-2}
                    marginBottom={3}
                    fontSize="0.8em"
                  >
                    From
                    {' ' + moment(firstAllowedTime).format('DD.MM.yyyy')}
                  </FormRow>
                </Box>
              </Box>
              <FormRow>
                <FormField
                  label="Suggestion ranking"
                  width={isMobileView ? 350 : 250}
                  hasError={!!errors.objective}
                >
                  <Select
                    data-testid="select-suggestion-ranking"
                    {...register('objective')}
                    options={suggestionRankingOptions}
                    required
                  />
                </FormField>
              </FormRow>
            </Box>
          </FormSection>
          <FormSection title="Maximum delays (days)">
            <FormRow>
              <Box display="flex" flexDirection="row" width="100%">
                <FormField
                  label="Load early"
                  width={200}
                  hasError={!!errors.violationLimitsD?.load?.early}
                >
                  <Input
                    type="number"
                    style={{ width: '5em' }}
                    step={0.1}
                    min={0}
                    placeholder="∞"
                    id="violationLimitsD.load.early"
                    {...register('violationLimitsD.load.early', {
                      setValueAs: stringToNumber,
                    })}
                    data-testid={`violationLimitsD.load.early`}
                  />
                </FormField>
                <FormField
                  label="Load late"
                  width={200}
                  hasError={!!errors.violationLimitsD?.load?.late}
                >
                  <Input
                    type="number"
                    style={{ width: '5em' }}
                    step={0.1}
                    min={0}
                    placeholder="∞"
                    id="violationLimitsD.load.late"
                    {...register('violationLimitsD.load.late', {
                      setValueAs: stringToNumber,
                    })}
                    data-testid={`violationLimitsD.load.late`}
                  />
                </FormField>
                <FormField
                  label="Discharge early"
                  width={200}
                  hasError={!!errors.violationLimitsD?.discharge?.early}
                >
                  <Input
                    type="number"
                    style={{ width: '5em' }}
                    step={0.1}
                    min={0}
                    placeholder="∞"
                    id="violationLimitsD.discharge.early"
                    {...register('violationLimitsD.discharge.early', {
                      setValueAs: stringToNumber,
                    })}
                    data-testid={`violationLimitsD.discharge.early`}
                  />
                </FormField>
                <FormField
                  label="Discharge late"
                  width={200}
                  hasError={!!errors.violationLimitsD?.discharge?.late}
                >
                  <Input
                    type="number"
                    style={{ width: '5em' }}
                    step={0.1}
                    min={0}
                    placeholder="∞"
                    id="violationLimitsD.discharge.late"
                    {...register('violationLimitsD.discharge.late', {
                      setValueAs: stringToNumber,
                    })}
                    data-testid={`violationLimitsD.discharge.late`}
                  />
                </FormField>
              </Box>
            </FormRow>
          </FormSection>
          <FormSection>
            <FormRow>
              <FormField
                label="Maximum ballast (nm)"
                width={320}
                hasError={!!errors.maxBallastDistanceNm}
              >
                <Input
                  type="number"
                  step={0.1}
                  min={0}
                  {...register('maxBallastDistanceNm', {
                    setValueAs: stringToNumber,
                  })}
                  data-testid={`maxBallastDistanceNm`}
                />
              </FormField>
            </FormRow>
          </FormSection>
          <FormSection>
            <FormRow>
              <FormField
                label="Allowed computation time (multiplier, default 1)"
                width={320}
                hasError={!!errors.computationBudget}
              >
                <Input
                  type="number"
                  step={0.1}
                  min={0.1}
                  max={100}
                  {...register('computationBudget', {
                    setValueAs: stringToNumber,
                  })}
                  disabled={numOrders === 1}
                  data-testid={`computationBudget`}
                />
              </FormField>
            </FormRow>
          </FormSection>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button variant="secondary" mr="auto" onPress={onCancel}>
          Cancel
        </Button>
        <Button
          data-testid="suggest-modal-confirm-button"
          variant="positive"
          disabled={Object.keys(errors).length > 0}
          onPress={onConfirm}
        >
          Confirm
        </Button>
      </ModalFooter>
    </Modal>
  );
}
